import { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetWeeklyAvailabilityQuery,
  useGetAbsencesQuery,
} from "../store/services/calendarApi";
import {
  checkRoomAvailability as checkRoomAvailabilityFn,
  checkTherapistAvailability as checkTherapistAvailabilityFn,
  resolveRoomAvailability,
} from "../firebase";
import { getWeekId } from "../utils/dateUtils";
import { findBlockingAbsence } from "../utils/absenceUtils";

/**
 * Live scheduling checks for an appointment being created/edited:
 *  - therapist double-booking (debounced, async — own AND other therapists,
 *    create AND edit; excludes the appointment being edited)
 *  - therapist absence covering the slot (warn / block per clinic policy)
 *  - overlap with a blocked-time window (warn only)
 *  - room availability hours + room double-booking (debounced, async)
 *
 * Mirrors the checks in AppointmentModal; extracted for the appointment wizard.
 */
export default function useAppointmentChecks({
  clinicId,
  therapistId,
  date, // "YYYY-MM-DD"
  startTime, // "HH:MM"
  duration, // minutes
  roomId,
  roomsEnabled,
  excludeAppointmentId = null,
  excludeGroupId = null,
}) {
  const [roomConflicts, setRoomConflicts] = useState([]);
  const [roomAvailabilityError, setRoomAvailabilityError] = useState(null);
  const [roomAvailabilityReason, setRoomAvailabilityReason] = useState(null);
  const [roomCheckPending, setRoomCheckPending] = useState(false);
  const debounceTimerRef = useRef(null);

  const [therapistConflicts, setTherapistConflicts] = useState([]);
  const [therapistCheckPending, setTherapistCheckPending] = useState(false);
  // True when the overlap query failed (e.g. a non-admin booking on a
  // colleague's calendar cannot read their appointments) — the UI then shows
  // nothing rather than a false "free" claim.
  const [therapistCheckFailed, setTherapistCheckFailed] = useState(false);
  const therapistDebounceRef = useRef(null);

  // Therapist overlap — debounced async check against Firestore.
  useEffect(() => {
    setTherapistConflicts([]);
    setTherapistCheckFailed(false);
    if (!clinicId || !therapistId || !date || !startTime || !duration) {
      setTherapistCheckPending(false);
      return undefined;
    }
    const startDateTime = new Date(`${date}T${startTime}`);
    if (Number.isNaN(startDateTime.getTime())) return undefined;

    let cancelled = false;
    if (therapistDebounceRef.current) clearTimeout(therapistDebounceRef.current);
    setTherapistCheckPending(true);
    therapistDebounceRef.current = setTimeout(async () => {
      try {
        const endDateTime = new Date(
          startDateTime.getTime() + parseInt(duration, 10) * 60000
        );
        const result = await checkTherapistAvailabilityFn(
          clinicId,
          therapistId,
          startDateTime,
          endDateTime,
          { excludeAppointmentId, excludeGroupId }
        );
        if (cancelled) return;
        setTherapistConflicts(result.conflicts || []);
      } catch (error) {
        if (cancelled) return;
        setTherapistCheckFailed(true);
        console.error("[CK-3820] Error checking therapist availability:", {
          errorCode: "CK-3820",
          errorMessage: error?.message,
          clinicId,
          therapistId,
          timestamp: new Date().toISOString(),
        });
      } finally {
        if (!cancelled) setTherapistCheckPending(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      if (therapistDebounceRef.current) clearTimeout(therapistDebounceRef.current);
    };
  }, [clinicId, therapistId, date, startTime, duration, excludeAppointmentId, excludeGroupId]);

  // First conflict keeps the historical name/shape the Schedule step renders.
  const overlappingAppointment = therapistConflicts[0] || null;

  // Absences of the therapist on that day (subscribed; cheap, tiny collection).
  // A failed query must not read as "no absence" — `absenceCheckFailed` lets
  // the UI say so (informational only, never a gate).
  const {
    data: dayAbsences = [],
    isError: absenceCheckFailed = false,
    error: absenceQueryError,
  } = useGetAbsencesQuery(
    { clinicId, from: date, to: date, therapistId },
    { skip: !clinicId || !date || !therapistId }
  );
  useEffect(() => {
    if (!absenceCheckFailed) return;
    console.error("[CK-3824] Error checking therapist absences:", {
      errorCode: "CK-3824",
      errorMessage: absenceQueryError?.message || String(absenceQueryError || ""),
      clinicId,
      therapistId,
      date,
      timestamp: new Date().toISOString(),
    });
  }, [absenceCheckFailed, absenceQueryError, clinicId, therapistId, date]);
  const therapistAbsence = useMemo(
    () => findBlockingAbsence(dayAbsences, date, startTime, duration),
    [dayAbsences, date, startTime, duration]
  );

  // Blocked-time window overlap — warn only, never blocks.
  const weekId = date ? getWeekId(new Date(date)) : null;
  const { data: blockWeekAvailability } = useGetWeeklyAvailabilityQuery(
    { therapistId, weekId },
    { skip: !therapistId || !weekId }
  );

  const overlappingBlock = useMemo(() => {
    if (!date || !startTime) return null;
    const dayBlocked =
      blockWeekAvailability?.slots?.find((d) => d.date === date)?.blocked || [];
    if (!dayBlocked.length) return null;
    const [h, m] = startTime.split(":").map(Number);
    const startMin = h * 60 + m;
    const endMin = startMin + (parseInt(duration, 10) || 45);
    return (
      dayBlocked.find((b) => {
        const [bh, bm] = b.start.split(":").map(Number);
        const [eh, em] = b.end.split(":").map(Number);
        return startMin < eh * 60 + em && bh * 60 + bm < endMin;
      }) || null
    );
  }, [blockWeekAvailability, date, startTime, duration]);

  // Room availability — debounced async check against Firestore.
  useEffect(() => {
    setRoomConflicts([]);
    setRoomAvailabilityError(null);
    setRoomAvailabilityReason(null);
    if (!roomsEnabled || !roomId || !date || !startTime || !duration || !clinicId) {
      return undefined;
    }

    let cancelled = false;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      setRoomCheckPending(true);
      try {
        const startDateTime = new Date(`${date}T${startTime}`);
        const endDateTime = new Date(
          startDateTime.getTime() + parseInt(duration, 10) * 60000
        );

        const availabilityResult = await resolveRoomAvailability(
          clinicId,
          roomId,
          startDateTime,
          endDateTime
        );
        if (cancelled) return;

        if (!availabilityResult.available) {
          setRoomAvailabilityError(availabilityResult.message);
          setRoomAvailabilityReason(availabilityResult);
          setRoomCheckPending(false);
          return;
        }

        const result = await checkRoomAvailabilityFn(
          clinicId,
          roomId,
          startDateTime,
          endDateTime,
          excludeAppointmentId
        );
        if (cancelled) return;

        if (!result.isAvailable) {
          setRoomConflicts(result.conflicts);
        }
      } catch (error) {
        console.error("[CK-3801] Error checking room availability:", {
          errorCode: "CK-3801",
          errorMessage: error?.message,
          clinicId,
          roomId,
          timestamp: new Date().toISOString(),
        });
      } finally {
        if (!cancelled) setRoomCheckPending(false);
      }
    }, 800);

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [clinicId, roomId, roomsEnabled, date, startTime, duration, excludeAppointmentId]);

  return {
    overlappingAppointment,
    therapistConflicts,
    therapistCheckPending,
    therapistCheckFailed,
    therapistAbsence,
    absenceCheckFailed,
    overlappingBlock,
    roomConflicts,
    roomAvailabilityError,
    roomAvailabilityReason,
    roomCheckPending,
  };
}
